import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from '../lib/icons';
import { SOCIAL_PLATFORMS } from '../data/social-config';
import { fetchNavItems, footerGroups, FALLBACK_NAV, type NavItem } from '../lib/nav-client';

// Social icon SVGs

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
    </svg>
  );
}

// ── Footer link (internal or external) ───────────────────────────────────────

function FooterLink({ item }: { item: NavItem }) {
  const cls = 'text-[#B0B0B0] hover:text-[#39FF14] transition-colors duration-200 text-sm';
  if (item.open_new_tab || item.route.startsWith('http')) {
    return <a href={item.route} target="_blank" rel="noopener noreferrer" className={cls}>{item.label}</a>;
  }
  return <Link to={item.route} className={cls}>{item.label}</Link>;
}

// ── Footer component ──────────────────────────────────────────────────────────

const Footer = () => {
  const [navItems, setNavItems] = useState<NavItem[]>(FALLBACK_NAV);

  useEffect(() => {
    fetchNavItems().then(setNavItems).catch(() => {/* keep fallback */});
  }, []);

  const groups = footerGroups(navItems);

  const yt = SOCIAL_PLATFORMS.youtube;
  const tt = SOCIAL_PLATFORMS.tiktok;
  const ig = SOCIAL_PLATFORMS.instagram;

  return (
    <footer className="bg-[#0D0D0D] text-[#E0E0E0] border-t border-gray-800">

      {/* ── Main footer grid ─────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src="/gamefix_&_pc_repair_.png" alt="GameFix Solutions Logo" className="h-10 w-10 object-contain" />
              <img src="/gamefix.png" alt="GameFix Solutions" className="h-14 object-contain" />
            </div>
            <p className="text-[#B0B0B0] text-sm mb-5 max-w-xs leading-relaxed">
              Professional PS5, Xbox, and Nintendo Switch repairs with lightning-fast turnaround. Board-level diagnostics. We also buy broken devices for cash.
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-4 mb-6">
              {yt.url && (
                <a href={yt.url} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
                  className="text-[#B0B0B0] hover:text-[#FF0000] transition-colors duration-200">
                  <YouTubeIcon className="h-5 w-5" />
                </a>
              )}
              {tt.url && (
                <a href={tt.url} target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                  className="text-[#B0B0B0] hover:text-[#E0E0E0] transition-colors duration-200">
                  <TikTokIcon className="h-5 w-5" />
                </a>
              )}
              {ig.url && (
                <a href={ig.url} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className="text-[#B0B0B0] hover:text-[#E1306C] transition-colors duration-200">
                  <InstagramIcon className="h-5 w-5" />
                </a>
              )}
            </div>

            {/* Locations */}
            <div className="space-y-4 text-sm text-[#B0B0B0]">
              <div>
                <p className="font-semibold text-[#E0E0E0] mb-1.5">San Diego</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 flex-shrink-0" /><span>(760) 214-7244</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <a href="https://maps.app.goo.gl/UDqXNnSx6yrmCJpw9?g_st=com.google.maps.preview.copy"
                      target="_blank" rel="noopener noreferrer"
                      className="hover:text-[#39FF14] transition-colors">
                      5375 Napa St, CA 92110
                    </a>
                  </div>
                </div>
              </div>
              <div>
                <p className="font-semibold text-[#E0E0E0] mb-1.5">Temecula</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 flex-shrink-0" /><span>(951) 216-8534</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <a href="https://maps.app.goo.gl/bbZsQWtNbftwZ3ZP8?g_st=ic"
                      target="_blank" rel="noopener noreferrer"
                      className="hover:text-[#39FF14] transition-colors">
                      View on Google Maps
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                <span>info@gamefixsolutions.com</span>
              </div>
            </div>
          </div>

          {/* Dynamic nav link columns */}
          {groups.map(group => (
            <div key={group.key}>
              <h4 className="text-sm font-bold text-[#E0E0E0] uppercase tracking-widest mb-4">{group.label}</h4>
              <ul className="space-y-2.5">
                {group.items.map(item => (
                  <li key={item.id}><FooterLink item={item} /></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── Service areas ───────────────────────────────────────────────── */}
      <div className="border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <p className="text-[10px] font-semibold text-gray-700 uppercase tracking-widest mb-2">
            Serving San Diego &amp; Temecula Valley
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
            {[
              { label: 'San Diego', slug: '/' },
              { label: 'Temecula', slug: '/temecula' },
              { label: 'Murrieta', slug: '/murrieta' },
              { label: 'Lake Elsinore', slug: '/lake-elsinore' },
              { label: 'Wildomar', slug: '/wildomar' },
              { label: 'Fallbrook', slug: '/fallbrook' },
              { label: 'Winchester', slug: '/winchester' },
            ].map(city => (
              <Link key={city.slug} to={city.slug} className="hover:text-[#39FF14] transition-colors">
                {city.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom bar ──────────────────────────────────────────────────── */}
      <div className="border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-[#B0B0B0] text-xs">© {new Date().getFullYear()} GameFix Solutions. All rights reserved.</p>
          <div className="flex items-center gap-5 text-xs">
            <a href="#" className="text-[#B0B0B0] hover:text-[#E0E0E0] transition-colors">Privacy Policy</a>
            <a href="#" className="text-[#B0B0B0] hover:text-[#E0E0E0] transition-colors">Terms of Service</a>
            <a href="#" className="text-[#B0B0B0] hover:text-[#E0E0E0] transition-colors">Warranty Info</a>
            <Link to="/admin/services" className="text-gray-700 hover:text-gray-500 transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
